export * from './types.js';
export const SPACE_IDS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'];
export const AXIAL = {
    '1': [1, -1], '2': [2, -1], '3': [3, -1],
    '4': [0, 0], '5': [1, 0], '6': [2, 0], '7': [3, 0],
    '8': [0, 1], '9': [1, 1], '10': [2, 1],
};
export const ADJACENCY = {
    '1': ['2', '4', '5'],
    '2': ['1', '3', '5', '6'],
    '3': ['2', '6', '7'],
    '4': ['1', '5', '8'],
    '5': ['1', '2', '4', '6', '8', '9'],
    '6': ['2', '3', '5', '7', '9', '10'],
    '7': ['3', '6', '10'],
    '8': ['4', '5', '9'],
    '9': ['5', '6', '8', '10'],
    '10': ['6', '7', '9'],
};
// Bright, red-green-colorblind-safe region colors. Maps use position and labels
// as redundant cues, so no gameplay information depends on hue alone.
const COLORS = {
    blue: ['#55C7F3', '#075985'],
    orange: ['#FFB84D', '#8A4B00'],
    purple: ['#D8A2F0', '#6B2D84'],
    yellow: ['#FFE66D', '#756000'],
    pink: ['#FF91B8', '#8B1E4B'],
};
function makeMap(id, name, tagline, boardBackground, regionSeeds) {
    const regionBySpace = new Map();
    for (const [regionId, , , , spaces] of regionSeeds) {
        for (const space of spaces)
            regionBySpace.set(space, regionId);
    }
    const regions = regionSeeds.map(([regionId, regionName, fill, stroke]) => ({ id: regionId, name: regionName, fill, stroke }));
    const colorCounts = new Map();
    return {
        id,
        name,
        tagline,
        boardBackground,
        regions,
        spaces: SPACE_IDS.map((spaceId) => {
            const regionId = regionBySpace.get(spaceId) ?? (() => { throw new Error(`Missing region for ${spaceId}`); })();
            const region = regions.find((item) => item.id === regionId) ?? (() => { throw new Error(`Missing region data for ${regionId}`); })();
            const colorNumber = (colorCounts.get(regionId) ?? 0) + 1;
            colorCounts.set(regionId, colorNumber);
            return { id: spaceId, name: `${region.name} ${colorNumber}`, regionId, adjacent: [...ADJACENCY[spaceId]] };
        }),
    };
}
export const MAPS = {
    coindor: makeMap('coindor', 'Map A', 'Four regions in a balanced 4·3·2·1 layout.', '#F5FAFF', [
        ['copper-peaks', 'Blue', ...COLORS.blue, ['1', '2', '3', '4']],
        ['forests', 'Orange', ...COLORS.orange, ['5', '6', '8']],
        ['plains', 'Purple', ...COLORS.purple, ['7', '10']],
        ['silver-city', 'Yellow', ...COLORS.yellow, ['9']],
    ]),
    oyou: makeMap('oyou', 'Map B', 'Five regions with a central one-space keystone.', '#F4FBFF', [
        ['bay', 'Blue', ...COLORS.blue, ['1', '2', '5']],
        ['agean', 'Orange', ...COLORS.orange, ['3', '7']],
        ['coast', 'Purple', ...COLORS.purple, ['4', '8']],
        ['sea', 'Yellow', ...COLORS.yellow, ['9', '10']],
        ['isle', 'Pink', ...COLORS.pink, ['6']],
    ]),
    lava: makeMap('lava', 'Map C', 'One five-space region makes majority especially valuable.', '#FFF9F3', [
        ['lakes', 'Blue', ...COLORS.blue, ['1', '2', '4', '8', '9']],
        ['mount', 'Orange', ...COLORS.orange, ['5']],
        ['street', 'Purple', ...COLORS.purple, ['3', '6']],
        ['sands', 'Yellow', ...COLORS.yellow, ['7', '10']],
    ]),
    snowshire: makeMap('snowshire', 'Map D', 'Five compact regions reward precise control.', '#F8F7FF', [
        ['coldlake', 'Blue', ...COLORS.blue, ['1', '4']],
        ['palace', 'Orange', ...COLORS.orange, ['2']],
        ['freezpond', 'Purple', ...COLORS.purple, ['3', '7']],
        ['cones', 'Yellow', ...COLORS.yellow, ['5', '6', '8']],
        ['igloo', 'Pink', ...COLORS.pink, ['9', '10']],
    ]),
};
export const MAP_LIST = Object.values(MAPS);
export const getMap = (mapId) => MAPS[mapId];
const DIRECTIONS = [[1, 0], [1, -1], [0, -1], [-1, 0], [-1, 1], [0, 1]];
export function deriveAdjacency() {
    const byCoordinate = new Map(Object.entries(AXIAL).map(([id, [q, r]]) => [`${q},${r}`, id]));
    return Object.fromEntries(SPACE_IDS.map((id) => {
        const [q, r] = AXIAL[id];
        const neighbors = DIRECTIONS
            .map(([dq, dr]) => byCoordinate.get(`${q + dq},${r + dr}`))
            .filter((neighbor) => neighbor !== undefined)
            .sort((a, b) => Number(a) - Number(b));
        return [id, neighbors];
    }));
}
//# sourceMappingURL=index.js.map